import { supabase, isSupabaseConfigured } from './supabase.js';

/** @typedef {'buyer' | 'factory'} UserRole */

/** @type {{ user: import('@supabase/supabase-js').User | null, profile: { id: string, role: UserRole, company: string | null } | null, loading: boolean }} */
export const authState = {
  user: null,
  profile: null,
  loading: true,
};

const listeners = new Set();

export function onAuthChange(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function notify() {
  for (const fn of listeners) {
    try {
      fn(authState);
    } catch (err) {
      console.warn('auth listener error', err);
    }
  }
}

export function isSignedIn() {
  return Boolean(authState.user);
}

export function authReady() {
  return !authState.loading;
}

async function loadProfile(userId) {
  if (!supabase || !userId) return null;
  const { data, error } = await supabase
    .from('profiles')
    .select('id, role, company, created_at')
    .eq('id', userId)
    .maybeSingle();
  if (error) {
    console.warn('profile load failed:', error.message);
    return null;
  }
  return data;
}

/**
 * Ensure a profiles row exists (covers race before DB trigger / missing migration).
 * @param {import('@supabase/supabase-js').User} user
 * @param {{ role?: UserRole, company?: string }} [meta]
 */
export async function ensureProfile(user, meta = {}) {
  if (!supabase || !user) return null;
  const role = meta.role || user.user_metadata?.role || 'buyer';
  const company = meta.company ?? user.user_metadata?.company ?? null;
  const { data, error } = await supabase
    .from('profiles')
    .upsert(
      {
        id: user.id,
        role: role === 'factory' ? 'factory' : 'buyer',
        company: company || null,
      },
      { onConflict: 'id' },
    )
    .select('id, role, company, created_at')
    .single();
  if (error) {
    console.warn('profile upsert failed (run migration 003?):', error.message);
    return await loadProfile(user.id);
  }
  return data;
}

async function setSession(session) {
  authState.user = session?.user ?? null;
  if (authState.user) {
    authState.profile =
      (await loadProfile(authState.user.id)) ||
      (await ensureProfile(authState.user));
  } else {
    authState.profile = null;
  }
  authState.loading = false;
  notify();
}

export async function initAuth() {
  if (!isSupabaseConfigured || !supabase) {
    authState.loading = false;
    notify();
    return;
  }
  const { data } = await supabase.auth.getSession();
  await setSession(data.session);
  supabase.auth.onAuthStateChange((_event, session) => {
    setSession(session);
  });
}

/**
 * @param {{ email: string, password: string, role: UserRole, company?: string }} opts
 */
export async function signUp({ email, password, role, company }) {
  if (!supabase) throw new Error('Supabase is not configured');
  const safeRole = role === 'factory' ? 'factory' : 'buyer';
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: {
      data: {
        role: safeRole,
        company: company || '',
      },
    },
  });
  if (error) throw error;
  if (data.user) {
    authState.profile = await ensureProfile(data.user, {
      role: safeRole,
      company: company || '',
    });
  }
  return data;
}

/**
 * @param {{ email: string, password: string }} opts
 */
export async function signIn({ email, password }) {
  if (!supabase) throw new Error('Supabase is not configured');
  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password,
  });
  if (error) throw error;
  return data;
}

export async function signOut() {
  if (!supabase) return;
  const { error } = await supabase.auth.signOut();
  if (error) throw error;
}

export function displayName() {
  if (!authState.user) return '';
  return (
    authState.profile?.company ||
    authState.user.email ||
    authState.user.id.slice(0, 8)
  );
}
